import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";

import { listDepositStockStatusCounts, listDepositStockStatusCountsReset } from "../../actions";
import { type StockStatusCount, type StockStatusOption } from "./types";

export type StockStatusCounts = Partial<Record<StockStatusOption, number>>;

interface RootState {
  listDepositStockStatusCounts: {
    isFetching: boolean;
    errorMessage: string;
    data: StockStatusCount[] | { results: [] };
  };
}

/** Country-wide stock records per Stock Status option, for the marketplace tabs. */
export const useStockStatusCounts = () => {
  const dispatch = useDispatch<Dispatch<UnknownAction>>();
  const { isFetching, errorMessage, data } = useSelector(
    (state: RootState) => state.listDepositStockStatusCounts
  );

  const refresh = useCallback(() => {
    dispatch(listDepositStockStatusCounts() as unknown as UnknownAction);
  }, [dispatch]);

  useEffect(() => {
    refresh();

    return () => {
      dispatch(listDepositStockStatusCountsReset() as unknown as UnknownAction);
    };
  }, [dispatch, refresh]);

  const counts = useMemo<StockStatusCounts>(
    () =>
      (Array.isArray(data) ? data : []).reduce<StockStatusCounts>(
        (sums, { stock_status, count }) => ({ ...sums, [stock_status]: count }),
        {}
      ),
    [data]
  );

  return {
    counts,
    refresh,
    isLoading: isFetching || (!Array.isArray(data) && !errorMessage),
  };
};
